import {
	Button,
	Badge,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Calendar
} from '@/components/ui';
import { Filter, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useQueryParams } from '@/hooks';
import TransactionsFiltersSidebar from './TransactionsFiltersSidebar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { IHeaderPageControlsProps } from '@/interfaces';

const HeaderPageControls = ({ dateRange, onDateRangeChange, onQuickPeriod, isLoading }: IHeaderPageControlsProps) => {
	const { urlSearchParams } = useQueryParams(['type', 'category', 'creatorId']);

	// Check if any filters are applied
	const getActiveFiltersCount = () => {
		const typesCount = urlSearchParams.getAll('type').length;
		const categoriesCount = urlSearchParams.getAll('category').length;
		const creatorsCount = urlSearchParams.getAll('creatorId').length;
		return typesCount + categoriesCount + creatorsCount;
	};

	const hasActiveFilters = getActiveFiltersCount() > 0;

	return (
		<div className="flex w-full gap-2 items-center">
			{/* Filter Button */}
			<Dialog>
				<DialogTrigger asChild>
					<Button variant={hasActiveFilters ? 'default' : 'outline'} size="sm" className="relative h-10 px-3 shrink-0">
						<Filter size={16} />
						{hasActiveFilters && (
							<Badge
								variant="secondary"
								className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full bg-primary text-primary-foreground border-2 border-background"
							>
								{getActiveFiltersCount()}
							</Badge>
						)}
					</Button>
				</DialogTrigger>
				<DialogContent className="max-w-md w-[calc(100%-2rem)] rounded-2xl">
					<DialogHeader>
						<DialogTitle>Filtres</DialogTitle>
					</DialogHeader>
					<div className="py-2">
						<TransactionsFiltersSidebar />
					</div>
				</DialogContent>
			</Dialog>

			{/* Date Selector (Center Piece) */}
			<div className="flex flex-1 items-center bg-muted/30 rounded-md border border-input h-10 overflow-hidden group hover:bg-muted/50 transition-colors">
				<Button
					variant="ghost"
					size="icon"
					className="h-full w-8 rounded-none hover:bg-background/50 border-r"
					onClick={() => onQuickPeriod('previous')}
					disabled={isLoading}
				>
					<ChevronLeft size={16} />
				</Button>

				<Popover>
					<PopoverTrigger asChild>
						<Button
							variant="ghost"
							className="flex-1 h-full justify-start text-left font-normal text-xs md:text-sm px-3 rounded-none focus-visible:ring-0"
							disabled={isLoading}
						>
							<CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
							<span className="truncate">
								{format(dateRange.from, 'dd MMM', { locale: fr })} - {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
							</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-auto p-0" align="center">
						<Calendar
							mode="range"
							defaultMonth={dateRange.from}
							selected={{ from: dateRange.from, to: dateRange.to }}
							onSelect={range => range && onDateRangeChange(range)}
							numberOfMonths={Math.min(2, typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 2)}
						/>
						<div className="flex gap-2 p-3 border-t">
							<Button variant="outline" size="sm" className="flex-1" onClick={() => onQuickPeriod('current')}>
								Ce mois
							</Button>
							<Button variant="outline" size="sm" className="flex-1" onClick={() => onQuickPeriod('previous')}>
								Mois préc.
							</Button>
						</div>
					</PopoverContent>
				</Popover>

				<Button
					variant="ghost"
					size="icon"
					className="h-full w-8 rounded-none hover:bg-background/50 border-l"
					onClick={() => onQuickPeriod('next')}
					disabled={isLoading}
				>
					<ChevronRight size={16} />
				</Button>
			</div>
		</div>
	);
};

export default HeaderPageControls;
